import { computed, ref, type Ref } from 'vue'
import { defineStore } from 'pinia'
import { CallService } from '@/services/telehealthApi/CallService'
import { NoticeType } from '@/stores/models/Notice'
import { useNoticeStore } from '@/stores/notice'
import { useConsultationRequestStore } from './consultationRequest'
import type {
  CallAccessDTO,
  ConsultationRequestDTO,
  ConsultationRequestWithCaseDTO,
  ScheduledCallAccessDTO,
  ScheduledConsultationDTO
} from '@/generated'

const ACTIVE_CALL_ID_KEY = 'active_call_id'
const ACTIVE_CALL_TYPE_KEY = 'active_call_type'

export interface CallAccess extends CallAccessDTO {
  type: 'direct' | 'scheduled'
}

export const useCallStore = defineStore('call', () => {
  const noticeStore = useNoticeStore()
  const {
    refreshIncomingConsultationRequestList,
    refreshOutgoingConsultationRequestList,
    refreshScheduledConsultationById,
    getIncomingDirectConsultationById,
    getIncomingScheduledConsultationById,
    getOutgoingDirectConsultationById,
    getOutgoingScheduledConsultationById,
    getDirectConsultationById
  } = useConsultationRequestStore()

  const callAccess: Ref<CallAccess | undefined> = ref()
  const consultationShareCode = ref<string | undefined>()
  const scheduledConsultationShareCode = ref<string | undefined>()
  const directConsultationRequest = ref<
    ConsultationRequestDTO | ConsultationRequestWithCaseDTO | undefined
  >()
  const scheduledConsultation = ref<ScheduledConsultationDTO | null>()

  const isInCall = computed<boolean>(() => !!callAccess.value)
  const roomId = computed<number | undefined>(() => callAccess.value?.roomId)
  const accessToken = computed<string | undefined>(() => callAccess.value?.accessToken)
  const callConsultationRequestId = computed<string | undefined>(
    () => callAccess.value?.consultationRequestId
  )
  const isInDirectCall = computed<boolean>(() => callAccess.value?.type === 'direct')
  const isInScheduledCall = computed<boolean>(() => callAccess.value?.type === 'scheduled')

  async function startCall(consultationRequestId: string): Promise<void> {
    try {
      const callAccessResponse = await CallService.joinCall(consultationRequestId)
      directConsultationRequest.value = resolveDirectConsultation(
        callAccessResponse.consultationRequestId
      )

      callAccess.value = {
        roomId: callAccessResponse.roomId,
        accessToken: callAccessResponse.accessToken,
        consultationRequestId,
        type: 'direct'
      }
      persistActiveCallId(consultationRequestId, 'direct')
    } catch (err) {
      noticeStore.add({
        type: NoticeType.ERROR,
        message: 'Failed to start the call'
      })
    }
  }

  async function getConsultationShareCode(consultationRequestId: string): Promise<void> {
    try {
      const _consultationShareCode = (
        await CallService.getConsultationShareCode(consultationRequestId)
      ).consultationShareCode

      consultationShareCode.value = `https://${window.location.host}/${_consultationShareCode}`
    } catch (err) {
      noticeStore.add({
        type: NoticeType.ERROR,
        message: 'Failed to get the share code'
      })
    }
  }

  async function getScheduledConsultationShareCode(consultationRequestId: string): Promise<void> {
    try {
      const _sharedConsultationShareCode = (
        await CallService.getScheduledConsultationShareCode(consultationRequestId)
      ).consultationShareCode

      scheduledConsultationShareCode.value = `https://${window.location.host}/consultation-details/${consultationRequestId}?shareCode=${_sharedConsultationShareCode}`
    } catch (err) {
      noticeStore.add({
        type: NoticeType.ERROR,
        message: 'Failed to get the share code'
      })
    }
  }

  async function joinCall(consultationRequestId: string): Promise<void> {
    try {
      await Promise.all([
        refreshOutgoingConsultationRequestList(),
        refreshIncomingConsultationRequestList()
      ])

      const callAccessResponse = await CallService.joinCall(consultationRequestId)
      directConsultationRequest.value = resolveDirectConsultation(
        callAccessResponse.consultationRequestId
      )

      callAccess.value = {
        roomId: callAccessResponse.roomId,
        accessToken: callAccessResponse.accessToken,
        consultationRequestId,
        type: 'direct'
      }
      persistActiveCallId(consultationRequestId, 'direct')
    } catch (err) {
      noticeStore.add({
        type: NoticeType.ERROR,
        message: 'Failed to join the call'
      })
    }
  }

  function isShareCodeAnUuid(shareCode: string): boolean {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      shareCode
    )
  }

  async function joinCallWithConsultationShareCode(consultationShareCode: string): Promise<void> {
    try {
      if (!isShareCodeAnUuid(consultationShareCode)) {
        return
      }
      const callAccessResponse =
        await CallService.joinWithConsultationShareCode(consultationShareCode)

      directConsultationRequest.value =
        resolveDirectConsultation(callAccessResponse.consultationRequestId) ??
        (await getDirectConsultationById(callAccessResponse.consultationRequestId))

      callAccess.value = {
        roomId: callAccessResponse.roomId,
        accessToken: callAccessResponse.accessToken,
        consultationRequestId: callAccessResponse.consultationRequestId,
        type: 'direct'
      }
      persistActiveCallId(callAccessResponse.consultationRequestId, 'direct') // <--- DODANE
    } catch (err) {
      noticeStore.add({
        type: NoticeType.ERROR,
        message: 'Failed to join the call with the share code'
      })
    }
  }

  async function endCall(consultationRequestId: string): Promise<void> {
    try {
      await CallService.endCall(consultationRequestId)
      callAccess.value = undefined
      clearActiveCallId()
    } catch (err) {
      noticeStore.add({
        type: NoticeType.ERROR,
        message: 'Failed to end the call'
      })
    }
  }

  function leaveCall() {
    callAccess.value = undefined
    clearActiveCallId()
  }

  async function joinScheduledConsultationCallById(
    consultationId: string,
    shareCode: string | undefined
  ): Promise<void> {
    try {
      let scheduledCallAccess: ScheduledCallAccessDTO | undefined

      if (shareCode) {
        scheduledCallAccess = await CallService.joinScheduledCallWithShareCode(shareCode)
      } else {
        scheduledCallAccess = await CallService.joinScheduledCall(consultationId)
        scheduledConsultation.value = resolveScheduledConsultation(
          scheduledCallAccess.scheduledConsultationRequestId
        )
      }

      callAccess.value = {
        roomId: scheduledCallAccess.roomId,
        accessToken: scheduledCallAccess.accessToken,
        consultationRequestId: scheduledCallAccess.scheduledConsultationRequestId,
        type: 'scheduled'
      }

      persistActiveCallId(consultationId, 'scheduled')

      await Promise.all([
        refreshOutgoingConsultationRequestList(),
        refreshIncomingConsultationRequestList(),
        refreshScheduledConsultationById(consultationId)
      ])
    } catch (error) {
      noticeStore.add({
        type: NoticeType.ERROR,
        message: 'Failed to scheduled join the call'
      })
      throw error
    }
  }

  function resolveDirectConsultation(requestId: string): ConsultationRequestDTO | undefined {
    return (
      getIncomingDirectConsultationById(requestId) ?? getOutgoingDirectConsultationById(requestId)
    )
  }

  function resolveScheduledConsultation(requestId: string): ScheduledConsultationDTO | null {
    return (
      getIncomingScheduledConsultationById(requestId) ??
      getOutgoingScheduledConsultationById(requestId)
    )
  }

  function persistActiveCallId(consultationId: string, type: 'direct' | 'scheduled') {
    sessionStorage.setItem(ACTIVE_CALL_ID_KEY, consultationId)
    sessionStorage.setItem(ACTIVE_CALL_TYPE_KEY, type)
  }

  function clearActiveCallId() {
    sessionStorage.removeItem(ACTIVE_CALL_ID_KEY)
    sessionStorage.removeItem(ACTIVE_CALL_TYPE_KEY)
  }

  async function tryRestoreActiveCall(): Promise<void> {
    const activeCallId = sessionStorage.getItem(ACTIVE_CALL_ID_KEY)
    const activeCallType = sessionStorage.getItem(ACTIVE_CALL_TYPE_KEY)

    if (!activeCallId) return

    try {
      if (activeCallType === 'direct') {
        await joinCall(activeCallId)
      } else if (activeCallType === 'scheduled') {
        await joinScheduledConsultationCallById(activeCallId, undefined)
      }

      if (!callAccess.value) {
        clearActiveCallId()
      }
    } catch (err) {
      console.warn('Failed to auto-restore active call', err)
      clearActiveCallId()
    }
  }
  return {
    isInCall,
    roomId,
    accessToken,
    callAccess,
    callConsultationRequestId,
    isInDirectCall,
    isInScheduledCall,
    consultationShareCode,
    scheduledConsultationShareCode,
    scheduledConsultation,
    directConsultationRequest,
    startCall,
    joinCall,
    endCall,
    leaveCall,
    getConsultationShareCode,
    getScheduledConsultationShareCode,
    joinCallWithConsultationShareCode,
    joinScheduledConsultationCallById,
    tryRestoreActiveCall
  }
})
